import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MdSystemUpdate, MdSave, MdAdd, MdDelete, MdRefresh } from "react-icons/md";

const AppVersionManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Form state
  const [latestVersion, setLatestVersion] = useState("");
  const [minSupportedVersion, setMinSupportedVersion] = useState("");
  const [isMandatory, setIsMandatory] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [storeUrl, setStoreUrl] = useState("");

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/app-config`
      );
      const data = res.data;
      setLatestVersion(data.latestVersion || "");
      setMinSupportedVersion(data.minSupportedVersion || "");
      setIsMandatory(data.isMandatory || false);
      setTitle(data.title || "");
      setMessage(data.message || "");
      setReleaseNotes(data.releaseNotes || []);
      setStoreUrl(data.storeUrl || "");
    } catch (error) {
      console.error("Error fetching app config:", error);
      setErrorMsg("Failed to fetch app config. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!latestVersion.trim()) {
      setErrorMsg("Latest Version is required");
      return;
    }
    try {
      setSaving(true);
      setErrorMsg("");
      setSuccessMsg("");
      await axios.put(`${process.env.REACT_APP_API_URL}/app-config`, {
        latestVersion: latestVersion.trim(),
        minSupportedVersion: minSupportedVersion.trim(),
        isMandatory,
        title: title.trim(),
        message: message.trim(),
        releaseNotes: releaseNotes.filter((n) => n.trim() !== ""),
        storeUrl: storeUrl.trim(),
      });
      setSuccessMsg("App config updated successfully! ✅");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (error) {
      console.error("Error updating app config:", error);
      setErrorMsg("Failed to update app config. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const addReleaseNote = () => setReleaseNotes([...releaseNotes, ""]);

  const updateReleaseNote = (index, value) => {
    const updated = [...releaseNotes];
    updated[index] = value;
    setReleaseNotes(updated);
  };

  const removeReleaseNote = (index) =>
    setReleaseNotes(releaseNotes.filter((_, i) => i !== index));

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Loading app config...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <MdSystemUpdate style={styles.headerIcon} />
          <div>
            <h1 style={styles.headerTitle}>App Version Manager</h1>
            <p style={styles.headerSubtitle}>
              Manage in-app update modal configuration
            </p>
          </div>
        </div>
        <button onClick={fetchConfig} style={styles.refreshBtn}>
          <MdRefresh style={{ fontSize: 18 }} />
          Refresh
        </button>
      </div>

      {successMsg && <div style={styles.successAlert}>{successMsg}</div>}
      {errorMsg && <div style={styles.errorAlert}>{errorMsg}</div>}

      <form onSubmit={handleSave}>
        <div style={styles.grid}>
          {/* Left — Version Info */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Version Info</h2>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Latest Version *</label>
              <input
                type="text"
                value={latestVersion}
                onChange={(e) => setLatestVersion(e.target.value)}
                placeholder="e.g. 5.0.0"
                style={styles.input}
                required
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Min Supported Version</label>
              <input
                type="text"
                value={minSupportedVersion}
                onChange={(e) => setMinSupportedVersion(e.target.value)}
                placeholder="e.g. 4.0.0"
                style={styles.input}
              />
              <p style={styles.hint}>
                Users below this version will be forced to update
              </p>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Store URL</label>
              <input
                type="text"
                value={storeUrl}
                onChange={(e) => setStoreUrl(e.target.value)}
                placeholder="https://play.google.com/..."
                style={styles.input}
              />
            </div>

            <div style={styles.toggleRow}>
              <div>
                <label style={styles.label}>Mandatory Update</label>
                <p style={styles.hint}>Users cannot skip this update</p>
              </div>
              <button
                type="button"
                onClick={() => setIsMandatory(!isMandatory)}
                style={{
                  ...styles.toggle,
                  backgroundColor: isMandatory ? "#e74c8a" : "#4a5568",
                }}
              >
                <div
                  style={{
                    ...styles.toggleKnob,
                    transform: isMandatory
                      ? "translateX(20px)"
                      : "translateX(0px)",
                  }}
                />
              </button>
            </div>
          </div>

          {/* Right — Modal Content */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Modal Content</h2>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What's New in HeartConnect! 🚀"
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="We have made your experience even faster..."
                style={styles.textarea}
                rows={3}
              />
            </div>

            <div style={styles.fieldGroup}>
              <div style={styles.releaseNotesHeader}>
                <label style={styles.label}>Release Notes</label>
                <button
                  type="button"
                  onClick={addReleaseNote}
                  style={styles.addNoteBtn}
                >
                  <MdAdd style={{ fontSize: 16 }} />
                  Add Note
                </button>
              </div>
              {releaseNotes.length === 0 && (
                <p style={styles.hint}>
                  No release notes yet. Click "Add Note" to add bullet points.
                </p>
              )}
              {releaseNotes.map((note, index) => (
                <div key={index} style={styles.noteRow}>
                  <span style={styles.noteBullet}>•</span>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => updateReleaseNote(index, e.target.value)}
                    placeholder={`Release note ${index + 1}`}
                    style={styles.noteInput}
                  />
                  <button
                    type="button"
                    onClick={() => removeReleaseNote(index)}
                    style={styles.removeNoteBtn}
                  >
                    <MdDelete style={{ fontSize: 16 }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={styles.saveContainer}>
          <button type="submit" disabled={saving} style={styles.saveBtn}>
            <MdSave style={{ fontSize: 20 }} />
            {saving ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  container: { padding: "24px", maxWidth: "1200px", margin: "0 auto" },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "400px",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid rgba(197,19,105,0.2)",
    borderTop: "4px solid #c51369",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: { marginTop: "16px", color: "#6b7280", fontSize: "14px" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: "12px" },
  headerIcon: { fontSize: "32px", color: "#c51369" },
  headerTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1a202c",
    margin: 0,
  },
  headerSubtitle: { fontSize: "14px", color: "#6b7280", margin: 0 },
  refreshBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 16px",
    backgroundColor: "#fff",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
  },
  successAlert: {
    padding: "12px 16px",
    backgroundColor: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: "8px",
    color: "#16a34a",
    marginBottom: "16px",
    fontSize: "14px",
  },
  errorAlert: {
    padding: "12px 16px",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "8px",
    color: "#dc2626",
    marginBottom: "16px",
    fontSize: "14px",
  },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" },
  card: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "24px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1a202c",
    margin: "0 0 20px 0",
  },
  fieldGroup: { marginBottom: "20px" },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    backgroundColor: "#fff",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    color: "#1a202c",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    padding: "10px 14px",
    backgroundColor: "#fff",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    color: "#1a202c",
    fontSize: "14px",
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  hint: { fontSize: "12px", color: "#9ca3af", margin: "4px 0 0 0" },
  toggleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "8px",
  },
  toggle: {
    width: "44px",
    height: "24px",
    borderRadius: "12px",
    border: "none",
    cursor: "pointer",
    position: "relative",
    transition: "background-color 0.2s",
    padding: 0,
  },
  toggleKnob: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    backgroundColor: "#fff",
    position: "absolute",
    top: "2px",
    left: "2px",
    transition: "transform 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
  },
  releaseNotesHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  addNoteBtn: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "6px 12px",
    backgroundColor: "rgba(197,19,105,0.08)",
    color: "#c51369",
    border: "1px solid rgba(197,19,105,0.2)",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
  },
  noteRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  noteBullet: { color: "#c51369", fontSize: "18px", fontWeight: "bold" },
  noteInput: {
    flex: 1,
    padding: "8px 12px",
    backgroundColor: "#fff",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    color: "#1a202c",
    fontSize: "14px",
    outline: "none",
  },
  removeNoteBtn: {
    padding: "6px",
    backgroundColor: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  saveContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "24px",
  },
  saveBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 28px",
    background: "#c51369",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    boxShadow: "0 2px 8px rgba(197,19,105,0.3)",
  },
};

export default AppVersionManager;
